import { Badge } from 'react-bootstrap';
import { MACHINE_STATE_BG, machineTitle } from '../../helpers/phaseMachines';

// Which lines a phase is on, as chips.
//
// One chip per MACHINE, never per run: a phase with four l75 runs reads
// "L75 ×4", not the same name four times. The rule that groups them lives in
// helpers/phaseMachines.js, shared with Project Monitor and Project Token State
// so the two screens cannot disagree about where a phase is.
//
// A running line carries a dot. That is the state somebody is actually looking
// for — "which machine is turning right now" — and a colour alone is a legend
// the reader has to remember.
const DOT = { running: '●', done: '✓', queued: '○' };

const MachineChips = ({
  machines = [],
  phaseLabel = null,
  size = 'sm',
  max = 0,
  // An explicit hex that replaces the state colour as the chip's FILL.
  //
  // Project Monitor passes the phase's own hue, which lets one compact row of
  // chips stand for all eight phases: the colour says which phase the machine
  // belongs to, so the mapping does not have to be carried by position — and a
  // chip per phase segment is what was pushing that column wide. The dot still
  // carries the state, so nothing is lost.
  tint = null,
  // No pill at all: the machine name in the caller's colour, next to whatever
  // it belongs to. Used where width is the binding constraint — the Phases
  // column fits eight of these inside one table cell, and a padded badge per
  // segment does not fit.
  plain = false,
  className = ''
}) => {
  const list = Array.isArray(machines) ? machines.filter(Boolean) : [];
  if (!list.length) return null;

  // A product routed across five lines would push the column wide enough to
  // squeeze its neighbours, so a cap is allowed — and what is hidden is said,
  // rather than simply dropped.
  const shown = max > 0 ? list.slice(0, max) : list;
  const hidden = list.length - shown.length;

  const fs = size === 'sm' ? 10 : 11;

  return (
    <span
      className={`d-inline-flex align-items-center gap-1 flex-wrap ${className}`}
    >
      {shown.map(m =>
        plain ? (
          <span
            key={m.machineId}
            title={machineTitle(m, { phaseLabel: m.phaseLabel || phaseLabel })}
            style={{
              fontSize: fs,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: tint || undefined,
              // Queued recedes, running is full strength. The dot says which.
              opacity: m.state === 'queued' ? 0.6 : 1
            }}
          >
            <span style={{ opacity: m.state === 'running' ? 1 : 0.7 }}>
              {DOT[m.state] || '○'}
            </span>
            {m.label}
            {m.count > 1 && (
              <span style={{ fontWeight: 400, opacity: 0.8 }}>×{m.count}</span>
            )}
          </span>
        ) : tint ? (
          // Tinted: the fill is the caller's hue and the state lives in the
          // dot's opacity, so a queued machine recedes without changing colour.
          // White text on a saturated fill reads the same on both themes, which
          // is the one thing the theme's own neutral badge did not.
          <span
            key={m.machineId}
            title={machineTitle(m, { phaseLabel: m.phaseLabel || phaseLabel })}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: '0 5px',
              fontSize: fs,
              fontWeight: 600,
              lineHeight: 1.5,
              borderRadius: 8,
              whiteSpace: 'nowrap',
              color: '#fff',
              background: tint,
              opacity: m.state === 'queued' ? 0.55 : 1
            }}
          >
            <span style={{ opacity: m.state === 'running' ? 1 : 0.7 }}>
              {DOT[m.state] || '○'}
            </span>
            {m.label}
            {m.count > 1 && (
              <span style={{ fontWeight: 400, opacity: 0.85 }}>×{m.count}</span>
            )}
          </span>
        ) : (
          <Badge
            key={m.machineId}
            bg={MACHINE_STATE_BG[m.state] || 'info'}
            variant="phoenix"
            className="fw-semibold"
            style={{ fontSize: fs, lineHeight: 1.4 }}
            title={machineTitle(m, { phaseLabel })}
          >
            <span style={{ opacity: m.state === 'running' ? 1 : 0.6 }}>
              {DOT[m.state] || '○'}
            </span>{' '}
            {m.label}
            {m.count > 1 && (
              <span style={{ fontWeight: 400, opacity: 0.85 }}> ×{m.count}</span>
            )}
          </Badge>
        )
      )}

      {hidden > 0 && (
        <span
          className="text-body-tertiary"
          style={{ fontSize: fs }}
          title={list
            .slice(shown.length)
            .map(m => machineTitle(m, { phaseLabel }))
            .join('\n')}
        >
          +{hidden}
        </span>
      )}
    </span>
  );
};

export default MachineChips;
